import { svgEl } from "./dom";

// Hand-authored 24px stroke icons, built with createElementNS from static data
// (no innerHTML, no icon font, no network). Icons are decorative: they are
// aria-hidden and always paired with a text label.

type Shape = readonly [tag: "path" | "circle" | "rect", attrs: Record<string, string | number>];

const p = (d: string): Shape => ["path", { d }];
const circle = (cx: number, cy: number, r: number): Shape => ["circle", { cx, cy, r }];
const rect = (x: number, y: number, width: number, height: number, rx = 0): Shape => [
  "rect",
  { x, y, width, height, rx },
];

const ICONS = {
  cloud: [p("M7 18a4 4 0 0 1-.5-7.97A5.5 5.5 0 0 1 17 8.5a4.5 4.5 0 0 1 .5 9.5H7z")],
  home: [p("M3 11l9-8 9 8"), p("M5 10v10h5v-6h4v6h5V10")],
  users: [circle(9, 8, 3.2), p("M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"), circle(17, 9, 2.5), p("M17 14c2.5 0 4.5 2 4.5 4.5")],
  monitor: [rect(3, 4, 18, 12, 2), p("M8 20h8M12 16v4")],
  send: [p("M21 3L3 10.5l7 3 3 7L21 3z"), p("M10 13.5L21 3")],
  grid: [rect(3, 3, 7, 7, 1.5), rect(14, 3, 7, 7, 1.5), rect(3, 14, 7, 7, 1.5), rect(14, 14, 7, 7, 1.5)],
  link: [
    p("M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1"),
    p("M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1"),
  ],
  chart: [p("M4 20v-9M10 20V4M16 20v-7M21 20H3")],
  plug: [p("M9 3v5M15 3v5M7 8h10v3a5 5 0 0 1-10 0V8zM12 16v5")],
  sliders: [p("M4 7h10M18 7h2M4 17h2M10 17h10"), circle(16, 7, 2), circle(8, 17, 2)],
  search: [circle(11, 11, 6), p("M20 20l-4-4")],
  "chevron-right": [p("M9 6l6 6-6 6")],
  "chevron-down": [p("M6 9l6 6 6-6")],
  "chevron-left": [p("M15 6l-6 6 6 6")],
  "arrow-right": [p("M5 12h14M13 6l6 6-6 6")],
  "arrow-up": [p("M12 19V5M6 11l6-6 6 6")],
  "arrow-down": [p("M12 5v14M6 13l6 6 6-6")],
  "alert-circle": [circle(12, 12, 9), p("M12 7.5v5M12 16.2v.3")],
  "alert-triangle": [p("M12 3.5L2.5 20h19L12 3.5z"), p("M12 10v4M12 17v.3")],
  "check-circle": [circle(12, 12, 9), p("M8 12.2l3 3 5-6")],
  check: [p("M5 12.5l4.5 4.5L19 7.5")],
  info: [circle(12, 12, 9), p("M12 11v5.5M12 7.8v.3")],
  file: [
    p("M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z"),
    p("M14 3v5h5M9 13h6M9 17h6"),
  ],
  external: [p("M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5")],
  sun: [circle(12, 12, 4), p("M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4")],
  moon: [p("M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z")],
  menu: [p("M4 6h16M4 12h16M4 18h16")],
  x: [p("M6 6l12 12M18 6L6 18")],
  calendar: [rect(3, 5, 18, 16, 2), p("M3 10h18M8 3v4M16 3v4")],
  clock: [circle(12, 12, 9), p("M12 7v5l3 2")],
  plus: [p("M12 5v14M5 12h14")],
  refresh: [p("M20 11a8 8 0 0 0-14.5-3.5M4 4v4h4M4 13a8 8 0 0 0 14.5 3.5M20 20v-4h-4")],
  building: [rect(5, 3, 14, 18, 1.5), p("M9 8h.01M15 8h.01M9 12h.01M15 12h.01M10 21v-4h4v4")],
  phone: [p("M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2z")],
  layers: [p("M12 3l9 5-9 5-9-5 9-5z"), p("M3 13l9 5 9-5")],
  desktop: [rect(3, 4, 18, 12, 2), p("M8 20h8M12 16v4")],
  mobile: [rect(7, 2.5, 10, 19, 2), p("M11 18.5h2")],
} satisfies Record<string, readonly Shape[]>;

export type IconName = keyof typeof ICONS;

export function icon(name: IconName, size = 18): SVGElement {
  const svg = svgEl(
    "svg",
    {
      viewBox: "0 0 24 24",
      width: size,
      height: size,
      fill: "none",
      stroke: "currentColor",
      "stroke-width": 1.8,
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "aria-hidden": "true",
      focusable: "false",
      class: "cp-icon",
    },
    ...ICONS[name].map(([tag, attrs]) => svgEl(tag, attrs)),
  );
  return svg;
}
